import { useState, useCallback } from 'react';
import { getAgentList, getAgentDetails } from '../../../../../services/agents';

/**
 * Hook that handles the full agent logic for the main chat.
 * Calls getAgentList() service directly and keeps local state.
 * Architecture: shared services + local module state (no global stores).
 */
export const useHomeChatAgent = () => {
  const [agents, setAgents] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [conversationStarters, setConversationStarters] = useState([]);
  const [isLoadingAgentDetails, setIsLoadingAgentDetails] = useState(false);

  /**
   * Loads the agent list from the service.
   * Maps the response for compatibility (id, photo_url, conversation_starters).
   */
  const fetchAgents = useCallback(async (params = {}) => {
    try {
      setIsLoadingAgents(true);
      setError(null);

      const response = await getAgentList(params);
      const agentsList = response.data?.agents || [];

      // Map API structure to expected format
      const mappedAgents = agentsList.map(agent => {
        const starters = (Array.isArray(agent.starters) && agent.starters.length > 0)
          ? agent.starters
          : (Array.isArray(agent.metadata?.conversation_starters) && agent.metadata.conversation_starters.length > 0)
            ? agent.metadata.conversation_starters
            : [];

        return {
          ...agent,
          id: agent.uuid, // Compatibility: id = uuid
          photo_url: agent.metadata?.imageUrl || agent.image || '',
          conversation_starters: starters
        };
      });

      setAgents(mappedAgents);
      return mappedAgents;
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError(err);
      setAgents([]);
      return [];
    } finally {
      setIsLoadingAgents(false);
    }
  }, []);

  /**
   * Selects an agent by ID and updates related state.
   * When an agent is selected, fetches the latest agent details from the API
   * to get the conversation_starters from metadata.
   */
  const selectAgent = useCallback(async (agentId, agentData = null) => {
    setSelectedAgentId(agentId);
    if (agentData) {
      setSelectedAgent(agentData);
    }
    setIsLoadingAgentDetails(true);

    try {
      // Always fetch latest agent details from API to get conversation_starters
      const response = await getAgentDetails(agentId);
      const fetchedAgent = response.data;

      if (fetchedAgent) {
        // Merge fetched data with passed agentData if provided (to preserve any local data)
        const mergedAgent = agentData 
          ? { ...agentData, ...fetchedAgent }
          : fetchedAgent;

        setSelectedAgent(mergedAgent);
        
        // Extract conversation_starters from metadata or direct property
        const starters = mergedAgent.conversation_starters || 
                        mergedAgent.metadata?.conversation_starters || 
                        [];
        
        setConversationStarters(Array.isArray(starters) ? starters : []);
      } else {
        // Fallback: if API call fails, use passed agentData or find in list
        if (agentData) {
          setSelectedAgent(agentData);
          const starters = agentData.conversation_starters || 
                          agentData.metadata?.conversation_starters || 
                          [];
          setConversationStarters(Array.isArray(starters) ? starters : []);
        } else {
          const agent = agents.find(
            (a) => a.uuid === agentId || a.id === agentId
          );
          if (agent) {
            setSelectedAgent(agent);
            const starters = agent.conversation_starters || 
                            agent.metadata?.conversation_starters || 
                            [];
            setConversationStarters(Array.isArray(starters) ? starters : []);
          } else {
            setSelectedAgent(null);
            setConversationStarters([]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching agent details:', error);
      
      // Fallback: use passed agentData or find in list
      if (agentData) {
        setSelectedAgent(agentData);
        const starters = agentData.conversation_starters || 
                        agentData.metadata?.conversation_starters || 
                        [];
        setConversationStarters(Array.isArray(starters) ? starters : []);
      } else {
        const agent = agents.find(
          (a) => a.uuid === agentId || a.id === agentId
        );
        if (agent) {
          setSelectedAgent(agent);
          const starters = agent.conversation_starters || 
                          agent.metadata?.conversation_starters || 
                          [];
          setConversationStarters(Array.isArray(starters) ? starters : []);
        } else {
          setSelectedAgent(null);
          setConversationStarters([]);
        }
      }
    } finally {
      setIsLoadingAgentDetails(false);
    }
  }, [agents]);

  /**
   * Clears the agent selection.
   */
  const clearAgent = useCallback(() => {
    setSelectedAgent(null);
    setSelectedAgentId(null);
    setConversationStarters([]);
  }, []);

  /**
   * Finds an agent by ID in the loaded list.
   */
  const getAgentById = useCallback((agentId) => {
    return agents.find(
      (a) => a.uuid === agentId || a.id === agentId
    ) || null;
  }, [agents]);

  return {
    // Agent list
    agents,
    isLoadingAgents,
    isLoadingAgentDetails,
    error,
    fetchAgents,
    // Selected agent
    selectedAgent,
    setSelectedAgent,
    selectedAgentId,
    setSelectedAgentId,
    conversationStarters,
    setConversationStarters,
    // Actions
    selectAgent,
    clearAgent,
    getAgentById,
  };
};
